import { useEffect, useMemo, useState } from "react";
import { useAppContextNonNull } from "@contexts/AppContext";
import EndpointsEnum from "@models/EndpointsEnum";
import ICategory from "@models/ICategory";
import IRoute from "@models/IRoute";
import IUser from "@models/IUser";
import IWorkflow from "@models/IWorkflow";
import IGetCategoriesDTO from "@models/DTO/IGetCategoriesDTO";
import IOnGetCategoriesDTO from "@models/DTO/IOnGetCategoriesDTO";
import IGetRoutesDTO from "@models/DTO/IGetRoutesDTO";
import IOnGetRoutesDTO from "@models/DTO/IOnGetRoutesDTO";
import ISupplierGetUsersDTO from "@models/DTO/ISupplierGetUsersDTO";
import ISuppliersOnGetUsersDTO from "@models/DTO/ISuppliersOnGetUsersDTO";
import IGetWorkflowsDTO from "@models/DTO/IGetWorkflowsDTO";
import IOnGetWorkflowsDTO from "@models/DTO/IOnGetWorkflowsDTO";
import IGetClientsDTO from "@models/DTO/IGetClientsDTO";
import IOnGetClientsDTO from "@models/DTO/IOnGetClientsDTO";
import IGetClientDTO from "@models/DTO/IGetClientDTO";
import IOnGetClientDTO from "@models/DTO/IOnGetClientDTO";
import IGetTagsDTO from "@models/DTO/IGetTagsDTO";
import IOnGetTagsDTO from "@models/DTO/IOnGetTagsDTO";
import OrderStatesEnum from "@models/OrderStatesEnum";
import UserTypesEnum from "@models/UserTypesEnum";
import AccessTypesEnum from "@models/AccessTypesEnum";
import OrderEntryService from "@services/OrderEntryService";
import { IFilterSelectOption } from "@models/IFilterConfig";
import { IOrderFilterLookups } from "@config/filters/ordersFilters";
import { requestOnce, useBaseDto, useVendorLookup } from "@hooks/shared/useSocketLookups";

/**
 * Loads the dynamic option lists the Orders filter config needs (categories, routes, users, workflows,
 * statuses) and exposes the typeahead lookup functions for vendors/clients/tags.
 */
export function useOrderFilterData() {
    const { socket, userModel, supplierModel } = useAppContextNonNull();
    const baseDto = useBaseDto();
    const vendorLookup = useVendorLookup();

    const [orderCategories, setOrderCategories] = useState<ICategory[]>([]);
    const [orderRoutes, setOrderRoutes] = useState<IRoute[]>([]);
    const [users, setUsers] = useState<IUser[]>([]);
    const [workflows, setWorkflows] = useState<IWorkflow[]>([]);

    const isSupplierUser = !!userModel.isSupplierUser;
    const isAdmin = isSupplierUser && userModel.userType?.id === UserTypesEnum.ADMIN;
    const canFilterByUser =
        isSupplierUser &&
        (isAdmin ||
            (userModel.userType?.id === UserTypesEnum.SALES && userModel.supplierUserToken?.Access_Type?.id === AccessTypesEnum.FULL));

    // ── Listeners ──
    useEffect(() => {
        const onCategories = (data: IOnGetCategoriesDTO) => {
            if (data.isOrderCategory) {
                setOrderCategories(data.categories || []);
            }
        };
        const onRoutes = (data: IOnGetRoutesDTO) => setOrderRoutes(data.routes || []);
        const onUsers = (data: ISuppliersOnGetUsersDTO) => setUsers(data.users || []);
        const onWorkflows = (data: IOnGetWorkflowsDTO) => setWorkflows(data.workflows || []);

        socket.on(EndpointsEnum.CategoriesOnGetCategories, onCategories);
        socket.on(EndpointsEnum.RoutesOnGetRoutes, onRoutes);
        socket.on(EndpointsEnum.SuppliersOnGetUsers, onUsers);
        socket.on(EndpointsEnum.WorkflowsOnGetWorkflows, onWorkflows);

        return () => {
            socket.off(EndpointsEnum.CategoriesOnGetCategories, onCategories);
            socket.off(EndpointsEnum.RoutesOnGetRoutes, onRoutes);
            socket.off(EndpointsEnum.SuppliersOnGetUsers, onUsers);
            socket.off(EndpointsEnum.WorkflowsOnGetWorkflows, onWorkflows);
        };
    }, [socket]);

    // ── Initial fetches (gated like the legacy filter); once per screen mount ──
    useEffect(() => {
        if (supplierModel.has_order_categories) {
            const payload: IGetCategoriesDTO = { ...baseDto(), isOrderCategory: true, onlyRootCategories: true };
            socket.emit(EndpointsEnum.CategoriesGetCategories, payload);
        }

        if (supplierModel.has_routes && isSupplierUser) {
            const payload: IGetRoutesDTO = { ...baseDto() };
            socket.emit(EndpointsEnum.RoutesGetRoutes, payload);
        }

        if (canFilterByUser) {
            const payload: ISupplierGetUsersDTO = { ...baseDto(), queryString: "" };
            socket.emit(EndpointsEnum.SuppliersGetUsers, payload);
        }

        if (supplierModel.has_multi_workflows && isSupplierUser) {
            const payload: IGetWorkflowsDTO = { ...baseDto() };
            socket.emit(EndpointsEnum.WorkflowsGetWorkflows, payload);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket]);

    // ── Statuses: workflow-derived when multi-workflow, otherwise the supplier's order steps ──
    const orderStatuses = useMemo(() => {
        if (supplierModel.has_multi_workflows && workflows.length) {
            return OrderEntryService.getSortedWorkflowStatus(workflows);
        }

        return OrderEntryService.getOrderSteps(supplierModel, OrderStatesEnum.PRELIMINARY).map((step) => ({
            id: step.order_state_id,
            name: step.name,
        }));
    }, [supplierModel, workflows]);

    // ── Typeahead lookups ──
    const lookups = useMemo<IOrderFilterLookups>(() => {
        const clients = (text: string): Promise<IFilterSelectOption[]> => {
            const payload: IGetClientsDTO = { ...baseDto(), queryString: `name=${encodeURIComponent(text)}` };

            return requestOnce<IOnGetClientsDTO>(socket, EndpointsEnum.ClientGetClients, EndpointsEnum.ClientOnGetClients, payload).then(
                (data) => (data.clients || []).map((client) => ({ label: client.name, value: client.guid }))
            );
        };

        const resolveClient = (guid: string): Promise<IFilterSelectOption | undefined> => {
            const payload: IGetClientDTO = { ...baseDto(), clientGuid: guid };

            return requestOnce<IOnGetClientDTO>(socket, EndpointsEnum.ClientGetClient, EndpointsEnum.ClientOnGetClient, payload).then(
                (data) => (data.client && data.client.guid === guid ? { label: data.client.name, value: data.client.guid } : undefined)
            );
        };

        const tags = (text: string): Promise<IFilterSelectOption[]> => {
            const payload: IGetTagsDTO = { ...baseDto(), name: text || "" };

            return requestOnce<IOnGetTagsDTO>(socket, EndpointsEnum.TagsGetTags, EndpointsEnum.TagsOnGetTags, payload).then((data) =>
                (data.tags || []).map((tag) => ({ label: tag.name, value: String(tag.id) }))
            );
        };

        // The tag list is small: resolve an id by listing all tags.
        const resolveTag = (id: string): Promise<IFilterSelectOption | undefined> =>
            tags("").then((all) => all.find((t) => String(t.value) === id));

        return { vendors: vendorLookup.vendors, resolveVendor: vendorLookup.resolveVendor, clients, resolveClient, tags, resolveTag };
    }, [socket, baseDto, vendorLookup]);

    return { orderCategories, orderRoutes, users, workflows, orderStatuses, lookups };
}
